import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ChangeEvent, type ClipboardEvent, type DragEvent, type ReactElement } from 'react';
import { Paperclip, Send, Check, CheckCheck, File as FileIcon, UserPlus, Tag as TagIcon, X, Mail, Ban, StickyNote, ArrowRightLeft, Search, Plus, Reply } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getConversation,
  loadHistory,
  mergeConversation,
  patchConversation,
  transferConversation,
  sendMessage,
  sendTranscriptToOperator,
  wsMessageNewToMessage,
  type Attachment,
  type AttachmentInput,
  type Conversation,
  type ConversationTagRef,
  type Message,
} from '../api/conversations';
import { listOperators, effectiveStatus } from '../api/operators';
import { StatusDot } from '../components/StatusDot';
import { createNote, listNotes, type ConversationNote } from '../api/notes';
import { getVisitorContext, type VisitorContext } from '../api/visitorContext';
import { listDepartments } from '../api/departments';
import { Link as RouterLink } from '@tanstack/react-router';
import { banVisitor } from '../api/bans';
import { getTenantSecurityPolicy } from '../api/tenant_policy';
import { createUpload, uploadFile } from '../api/uploads';
import { EmojiPicker } from '../components/EmojiPicker';
import { CloseControl } from '../components/CloseControl';
import { ConversationSearch } from '../components/ConversationSearch';
import { insertAtCursor, firstDroppedFile, firstPastedFile } from '../lib/composer';
import { isSendKey } from '../lib/composerKeys';
import { highlightParts } from '../lib/highlight';
import { parseLinks } from '../lib/links';
import { messageExcerpt } from '../lib/quote';
import { listCannedResponses, type CannedResponse } from '../api/cannedResponses';
import {
  assignTagToConversation,
  createConversationTag,
  listConversationTags,
  unassignTagFromConversation,
  type ConversationTag,
} from '../api/conversationTags';
import { tagPickerState, pickTagColor } from '../lib/tagPicker';
import { getApiClient } from '../api/factory';
import { createOperatorWs, type ConnectionState, type OperatorWs, type WsEnvelope } from '../ws';
import { useAuthStore } from '../store/auth';
import { ApiError } from '../api/client';
import { useT, type TFn } from '../i18n';
import { useHotkeys } from '../hooks/useHotkeys';

// Перепосылка typing.start пока оператор печатает, чтобы серверный
// TypingExpire (5s) не уронул индикатор. Должен быть < server expire / 2.
const TYPING_DEBOUNCE_MS = 2000;

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const ACCEPT_MIME = 'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,application/zip';

interface Props {
  conversationId: string;
}

function messagesKey(id: string) {
  return ['messages', id] as const;
}

function wsBaseUrl(): string {
  const origin = (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '';
  if (origin === '') {
    const loc = window.location;
    const proto = loc.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${loc.host}/ws/v1/operator`;
  }
  return origin.replace(/^http/, 'ws') + '/ws/v1/operator';
}

function newClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Маловероятный fallback. Не пытаемся быть RFC4122-точными — backend
  // принимает любой UUID v4-формат, а от crypto.randomUUID этого хватает.
  return 'cli-' + Math.random().toString(16).slice(2) + Date.now().toString(16);
}

interface OptimisticMessage extends Message {
  pending?: boolean;
  failed?: boolean;
}

function mergeOptimistic(
  prev: OptimisticMessage[] | undefined,
  incoming: OptimisticMessage,
): OptimisticMessage[] {
  const base = prev ?? [];
  // Сначала пробуем найти по серверному id (если уже прилетал echo).
  const byId = base.findIndex((m) => m.id === incoming.id);
  if (byId !== -1) {
    const next = base.slice();
    next[byId] = incoming;
    // Если incoming — реальное сообщение (id без tmp-префикса) и у него
    // есть clientId, заодно вычищаем tmp-двойники с тем же clientId.
    // Иначе через React Query polling может остаться «отправка...»-плейсхолдер
    // выше реального сообщения, пока кэш не invalidate'нется (ANX-027).
    if (incoming.clientId && !incoming.id.startsWith('tmp-')) {
      return next.filter(
        (m, i) =>
          i === byId || !(m.id.startsWith('tmp-') && m.clientId === incoming.clientId),
      );
    }
    return next;
  }
  // Иначе — по clientId, чтобы заменить optimistic-плейсхолдер.
  if (incoming.clientId) {
    const byClient = base.findIndex((m) => m.clientId === incoming.clientId);
    if (byClient !== -1) {
      const next = base.slice();
      next[byClient] = incoming;
      return next;
    }
  }
  return [...base, incoming];
}

/**
 * ANX-049 — мёрж серверной страницы (свежая или более старая) с кэшем.
 * Сохраняет уже подгруженные старые сообщения (polling шлёт только свежие 30),
 * вычищает tmp-optimistic'и с подтверждённым clientId, сортирует по seq
 * (pending seq=0 — в конец).
 */
function mergeServerMessages(
  cached: OptimisticMessage[],
  fresh: Message[],
): OptimisticMessage[] {
  const byId = new Map<string, OptimisticMessage>();
  for (const m of cached) byId.set(m.id, m);
  const confirmedClientIds = new Set<string>();
  for (const m of fresh) {
    byId.set(m.id, { ...m });
    if (m.clientId) confirmedClientIds.add(m.clientId);
  }
  const out = Array.from(byId.values()).filter(
    (m) =>
      !(m.id.startsWith('tmp-') && m.clientId !== undefined && confirmedClientIds.has(m.clientId)),
  );
  out.sort((a, b) => {
    const as = a.seq > 0 ? a.seq : Number.MAX_SAFE_INTEGER;
    const bs = b.seq > 0 ? b.seq : Number.MAX_SAFE_INTEGER;
    if (as !== bs) return as - bs;
    return a.createdAt.localeCompare(b.createdAt);
  });
  return out;
}

export function ConversationView({ conversationId }: Props) {
  const queryClient = useQueryClient();
  const session = useAuthStore((s) => s.session);
  const t = useT();
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  // ANX-119 — сообщение, на которое отвечаем (цитата). null → обычная отправка.
  const [replyTo, setReplyTo] = useState<OptimisticMessage | null>(null);
  const [wsState, setWsState] = useState<ConnectionState>('connecting');
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  // ANX-077 — поиск по сообщениям внутри диалога.
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlightQuery, setHighlightQuery] = useState('');
  const [activeSeq, setActiveSeq] = useState(0);
  const [visitorTyping, setVisitorTyping] = useState(false);
  const [visitorReadSeq, setVisitorReadSeq] = useState(0);
  const [pickerActive, setPickerActive] = useState(false);
  const [pickerIndex, setPickerIndex] = useState(0);
  const [reachedStart, setReachedStart] = useState(false);
  // ANX-063 — режим composer'а: ответ visitor'у или внутренняя заметка.
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  // Упоминания, выбранные через @-autocomplete. При отправке фильтруем по
  // тому, чьё @имя ещё осталось в теле.
  const [mentions, setMentions] = useState<{ id: string; name: string }[]>([]);
  const [mentionActive, setMentionActive] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const wsRef = useRef<OperatorWs | null>(null);
  // ANX-116 — авто-grow composer'а под многострочный текст (Shift+Enter).
  // height:auto сбрасывает прошлую высоту перед измерением scrollHeight.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingActiveRef = useRef(false);
  const loadingOlderRef = useRef(false);

  // Шаблоны грузим один раз — список редко меняется. invalidate сделает
  // CannedResponsesPage при правках.
  const { data: cannedAll } = useQuery<CannedResponse[]>({
    queryKey: ['canned_responses'],
    queryFn: () => listCannedResponses(getApiClient()),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  // Если draft начинается с '/' — фильтруем шаблоны по shortcut prefix или
  // title substring. Иначе picker скрыт.
  const matchedCanned = useMemo(() => {
    if (!cannedAll || !draft.startsWith('/')) return [];
    const q = draft.slice(1).toLowerCase();
    if (q === '') return cannedAll.slice(0, 8);
    return cannedAll
      .filter((cr) => {
        const sc = (cr.shortcut ?? '').toLowerCase();
        return sc.startsWith('/' + q) || cr.title.toLowerCase().includes(q);
      })
      .slice(0, 8);
  }, [cannedAll, draft]);

  function applyCanned(cr: CannedResponse) {
    setDraft(cr.body);
    setPickerActive(false);
    setPickerIndex(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  // ANX-063 — заметки диалога (видны только операторам). Polling 5s, как
  // основной канал доставки: заметки НЕ бродкастятся в WS (visitor в той же
  // комнате), упомянутый получает push отдельно.
  const { data: notes } = useQuery<ConversationNote[]>({
    queryKey: ['notes', conversationId],
    queryFn: () => listNotes(getApiClient(), conversationId),
    refetchInterval: 5_000,
  });

  // Операторы tenant'а для @-autocomplete (только в note-режиме).
  const { data: teamOperators } = useQuery({
    queryKey: ['operators'],
    queryFn: () => listOperators(getApiClient()),
    staleTime: 60_000,
    enabled: mode === 'note',
  });

  // Матч @-токена в хвосте draft'а (note-режим). Имя оператора может содержать
  // пробелы, но триггер по только что напечатанному `@\w*`.
  const matchedOperators = useMemo(() => {
    if (mode !== 'note' || !mentionActive || !teamOperators) return [];
    const m = /@(\w*)$/.exec(draft);
    if (!m) return [];
    const q = m[1].toLowerCase();
    return teamOperators
      .filter((o) => o.id !== session?.operator.id)
      .filter((o) => q === '' || o.name.toLowerCase().includes(q) || o.email.toLowerCase().includes(q))
      .slice(0, 8);
  }, [mode, mentionActive, teamOperators, draft, session]);

  function applyMention(op: { id: string; name: string }) {
    setDraft((d) => d.replace(/@(\w*)$/, `@${op.name} `));
    setMentions((prev) => (prev.some((x) => x.id === op.id) ? prev : [...prev, { id: op.id, name: op.name }]));
    setMentionActive(false);
    setMentionIndex(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function sendTypingStart() {
    const ws = wsRef.current;
    if (!ws) return;
    if (!typingActiveRef.current) {
      ws.send({ type: 'typing.start', payload: { conversation_id: conversationId } });
      typingActiveRef.current = true;
    }
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      ws.send({ type: 'typing.stop', payload: { conversation_id: conversationId } });
      typingActiveRef.current = false;
      typingTimerRef.current = null;
    }, TYPING_DEBOUNCE_MS);
  }

  function sendTypingStop() {
    const ws = wsRef.current;
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (typingActiveRef.current && ws) {
      ws.send({ type: 'typing.stop', payload: { conversation_id: conversationId } });
    }
    typingActiveRef.current = false;
  }

  // Conversation snapshot — для шапки с visitor-контактами (ANX-029).
  // Лёгкий запрос, отдельный от messages: invalidate'ится только если
  // visitor сам обновил контакты (WS-нотификация в будущем; пока polling 30s).
  const { data: conv } = useQuery<Conversation>({
    queryKey: ['conversation', conversationId],
    queryFn: () => getConversation(getApiClient(), conversationId),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const { data: messages, isLoading } = useQuery({
    queryKey: messagesKey(conversationId),
    queryFn: async () => {
      // ANX-049: грузим свежие 30 (окно 90 дней на backend). mergeServerMessages
      // сохраняет уже подгруженные scroll-up'ом старые сообщения и pending
      // optimistic'и (ANX-027) — polling только обновляет свежий хвост.
      const { messages: fresh } = await loadHistory(getApiClient(), conversationId, { limit: 30 });
      const cached =
        queryClient.getQueryData<OptimisticMessage[]>(messagesKey(conversationId)) ?? [];
      return mergeServerMessages(cached, fresh);
    },
    // 5s fallback-polling на случай если WS пропустит message.new.
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });

  // ANX-049 — при смене диалога сбрасываем флаг «достигли начала истории».
  useEffect(() => {
    setReachedStart(false);
  }, [conversationId]);

  // WS-подписка на одну conversation.
  const token = session?.access;
  useEffect(() => {
    if (!token) return;
    const ws = createOperatorWs({
      url: wsBaseUrl(),
      token,
      conversationId,
      onState: (state) => {
        setWsState(state);
        // ANX-027: после WS reconnect подтянуть свежие сообщения за
        // период disconnect. listMessages уже идемпотентен (dedup по id).
        if (state === 'online') {
          void queryClient.invalidateQueries({ queryKey: messagesKey(conversationId) });
        }
      },
      onMessage: (env: WsEnvelope) => {
        if (env.type === 'typing.update') {
          const p = env.payload as { party?: string; typing?: boolean } | undefined;
          if (p && p.party === 'visitor' && typeof p.typing === 'boolean') {
            setVisitorTyping(p.typing);
          }
          return;
        }
        if (env.type === 'read.update') {
          const p = env.payload as { party?: string; last_read_seq?: number } | undefined;
          if (p && p.party === 'visitor' && typeof p.last_read_seq === 'number') {
            setVisitorReadSeq((prev) => (p.last_read_seq! > prev ? p.last_read_seq! : prev));
          }
          return;
        }
        if (env.type !== 'message.new') return;
        // ANX-099: payload — snake_case, обязателен маппинг в Message.
        // Прямой каст ронял view на attachments (mimeType === undefined).
        const m = wsMessageNewToMessage(env.payload);
        if (!m) return;
        queryClient.setQueryData<OptimisticMessage[]>(
          messagesKey(conversationId),
          (prev) => mergeOptimistic(prev, m),
        );
      },
    });
    wsRef.current = ws;
    ws.connect();
    return () => {
      sendTypingStop();
      ws.close();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, queryClient, token]);

  // Авто-отметка прочитанного: при появлении новых visitor-сообщений
  // отсылаем message.read с самым последним message_id.
  const lastVisitorMessage = useMemo(() => {
    if (!messages || messages.length === 0) return null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.senderType === 'visitor' && !m.id.startsWith('tmp-')) return m;
    }
    return null;
  }, [messages]);
  useEffect(() => {
    if (!lastVisitorMessage) return;
    const ws = wsRef.current;
    if (!ws) return;
    ws.send({
      type: 'message.read',
      payload: { conversation_id: conversationId, message_id: lastVisitorMessage.id },
    });
  }, [conversationId, lastVisitorMessage?.id]);

  // Авто-скролл вниз при поступлении новых сообщений.
  const lastSeq = useMemo(
    () => (messages && messages.length ? messages[messages.length - 1].seq : 0),
    [messages],
  );
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lastSeq]);

  // ANX-049 — reverse infinite-scroll: подгрузка старых при прокрутке к верху.
  // seq в conversation непрерывный (MAX+1), поэтому oldest>1 ⇒ есть старее;
  // reachedStart ловит границу окна 90 дней (сервер вернул has_more=false).
  const oldestLoadedSeq = useMemo(() => {
    if (!messages) return 0;
    let min = 0;
    for (const m of messages) {
      if (m.seq > 0 && (min === 0 || m.seq < min)) min = m.seq;
    }
    return min;
  }, [messages]);
  const canLoadOlder = !reachedStart && oldestLoadedSeq > 1;

  async function loadOlder() {
    const el = scrollRef.current;
    if (!el || loadingOlderRef.current || !canLoadOlder) return;
    loadingOlderRef.current = true;
    const prevH = el.scrollHeight;
    const prevT = el.scrollTop;
    try {
      const { messages: older, hasMore } = await loadHistory(getApiClient(), conversationId, {
        beforeSeq: oldestLoadedSeq,
      });
      if (older.length === 0 || !hasMore) setReachedStart(true);
      if (older.length > 0) {
        queryClient.setQueryData<OptimisticMessage[]>(
          messagesKey(conversationId),
          (prev) => mergeServerMessages(prev ?? [], older),
        );
        // Сохраняем позицию: лента выросла сверху на (newH - prevH).
        requestAnimationFrame(() => {
          const e2 = scrollRef.current;
          if (e2) e2.scrollTop = e2.scrollHeight - prevH + prevT;
        });
      }
    } catch {
      // повторный scroll попробует снова
    } finally {
      loadingOlderRef.current = false;
    }
  }

  function onMessagesScroll() {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop < 48 && canLoadOlder && !loadingOlderRef.current) {
      void loadOlder();
    }
  }

  // ANX-077 — переход к найденному сообщению: подгружаем историю окнами, пока
  // нужный seq не окажется в кэше (или не упрёмся в начало), затем скроллим к
  // его DOM-узлу. Авто-скролл вниз не мешает — он завязан на lastSeq (новейшее),
  // а мы добавляем только старые сообщения.
  const onSearchNavigate = useCallback(
    async (seq: number) => {
      setActiveSeq(seq);
      const key = messagesKey(conversationId);
      for (let guard = 0; guard < 40; guard++) {
        const cur = queryClient.getQueryData<OptimisticMessage[]>(key) ?? [];
        if (cur.some((m) => m.seq === seq)) break;
        let oldest = 0;
        for (const m of cur) if (m.seq > 0 && (oldest === 0 || m.seq < oldest)) oldest = m.seq;
        if (oldest <= 1) break;
        let res;
        try {
          res = await loadHistory(getApiClient(), conversationId, { beforeSeq: oldest });
        } catch {
          break;
        }
        if (res.messages.length > 0) {
          queryClient.setQueryData<OptimisticMessage[]>(key, (prev) =>
            mergeServerMessages(prev ?? [], res.messages),
          );
        }
        if (!res.hasMore || res.messages.length === 0) break;
      }
      requestAnimationFrame(() => {
        const node = scrollRef.current?.querySelector(`[data-seq="${seq}"]`);
        if (node) node.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    },
    [conversationId, queryClient],
  );

  const onSearchClose = useCallback(() => {
    setSearchOpen(false);
    setHighlightQuery('');
    setActiveSeq(0);
  }, []);

  // ANX-119 — индекс сообщений по id для резолва цитаты (reply) в пузырях.
  const messagesById = useMemo(() => {
    const map = new Map<string, OptimisticMessage>();
    for (const m of messages ?? []) map.set(m.id, m);
    return map;
  }, [messages]);

  async function doSend(body: string, attachments?: Attachment[]) {
    const clientId = newClientId();
    const rid = replyTo?.id;
    setReplyTo(null);
    setSendError(null);
    const optimistic: OptimisticMessage = {
      id: 'tmp-' + clientId,
      conversationId,
      seq: 0,
      senderType: 'operator',
      clientId,
      type: attachments && attachments.length > 0 ? 'image' : 'text',
      body,
      createdAt: new Date().toISOString(),
      pending: true,
      ...(attachments && attachments.length > 0 ? { attachments } : {}),
      ...(rid ? { replyToId: rid } : {}),
    };
    queryClient.setQueryData<OptimisticMessage[]>(
      messagesKey(conversationId),
      (prev) => mergeOptimistic(prev, optimistic),
    );

    try {
      const sentInput: {
        clientId: string;
        body: string;
        attachments?: AttachmentInput[];
        replyToId?: string;
      } = {
        clientId,
        body,
      };
      if (rid) sentInput.replyToId = rid;
      if (attachments && attachments.length > 0) {
        sentInput.attachments = attachments.map((a) => ({
          key: a.key,
          name: a.name,
          size: a.size,
          mimeType: a.mimeType,
        }));
      }
      const sent = await sendMessage(getApiClient(), conversationId, sentInput);
      queryClient.setQueryData<OptimisticMessage[]>(
        messagesKey(conversationId),
        (prev) => mergeOptimistic(prev, sent),
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('conversation.sendError');
      setSendError(msg);
      queryClient.setQueryData<OptimisticMessage[]>(
        messagesKey(conversationId),
        (prev) =>
          mergeOptimistic(prev, {
            ...optimistic,
            failed: true,
            pending: false,
          }),
      );
    }
  }

  // ANX-063 — отправка внутренней заметки. Упоминания берём только те, чьё
  // @имя ещё присутствует в теле (оператор мог удалить вставленный токен).
  async function doSendNote(body: string) {
    setSendError(null);
    const ids = mentions.filter((m) => body.includes('@' + m.name)).map((m) => m.id);
    setMentions([]);
    try {
      const created = await createNote(getApiClient(), conversationId, {
        body,
        ...(ids.length > 0 ? { mentionedOperatorIds: ids } : {}),
      });
      queryClient.setQueryData<ConversationNote[]>(
        ['notes', conversationId],
        (prev) => [...(prev ?? []), created],
      );
    } catch (err) {
      setSendError(err instanceof ApiError ? err.message : t('conversation.note.error'));
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const body = draft.trim();
    if (body === '') return;
    setDraft('');
    if (mode === 'note') {
      await doSendNote(body);
      return;
    }
    sendTypingStop();
    await doSend(body);
  }

  // ANX-044 — клавиатурные шорткаты в контексте открытого диалога.
  //  - mod+enter в composer'е → отправить (allowInEditable, иначе keydown в input игнорится).
  //  - `/` вне input'а → сфокусировать composer и вписать `/`, чтобы открылся picker шаблонов.
  //  - `e` вне input'а → закрыть текущий диалог (если он ещё open).
  useHotkeys(
    {
      'mod+enter': () => {
        const body = draft.trim();
        if (body === '' || uploading) return;
        setDraft('');
        if (mode === 'note') {
          void doSendNote(body);
          return;
        }
        sendTypingStop();
        void doSend(body);
      },
      '/': () => {
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        // Если уже что-то напечатано — не перетираем; иначе ставим `/` для slash-picker'а.
        if (draft === '') {
          setDraft('/');
          setPickerActive(true);
          setPickerIndex(0);
        }
      },
      e: () => {
        if (!conv || conv.status === 'closed') return;
        void (async () => {
          try {
            const updated = await patchConversation(getApiClient(), conversationId, {
              status: 'closed',
            });
            queryClient.setQueryData<Conversation>(['conversation', conversationId], (prev) =>
              mergeConversation(prev, updated),
            );
            void queryClient.invalidateQueries({ queryKey: ['conversations'] });
          } catch {
            // Сетевой сбой — оператор всегда может повторить кликом по статусу.
          }
        })();
      },
    },
    { allowInEditable: ['mod+enter'] },
  );

  // ANX-076 — единый upload-флоу, переиспользуемый paperclip-кнопкой,
  // drag&drop'ом и вставкой из буфера. Загружает файл в R2 и шлёт его
  // attachment'ом вместе с текущим черновиком.
  async function uploadAndSend(file: File) {
    if (mode !== 'reply' || uploading) return;
    setSendError(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setSendError(t('conversation.uploadTooLarge'));
      return;
    }
    setUploading(true);
    try {
      const up = await createUpload(getApiClient(), {
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
      });
      await uploadFile({ uploadUrl: up.uploadUrl, mimeType: up.mimeType, file });
      const att: Attachment = {
        key: up.key,
        name: file.name,
        size: file.size,
        mimeType: up.mimeType,
      };
      await doSend(draft.trim(), [att]);
      setDraft('');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('conversation.uploadError');
      setSendError(msg);
    } finally {
      setUploading(false);
    }
  }

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.currentTarget.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    await uploadAndSend(file);
  }

  // ANX-076 — emoji из пикера вставляется в позицию каретки input'а.
  function insertEmoji(em: string) {
    const input = inputRef.current;
    const start = input?.selectionStart ?? draft.length;
    const end = input?.selectionEnd ?? draft.length;
    const { value, caret } = insertAtCursor(draft, start, end, em);
    setDraft(value);
    // Возвращаем фокус и каретку после ре-рендера.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }

  // ANX-076 — drag&drop файла на composer (только режим «Ответ»).
  function handleDragOver(e: DragEvent<HTMLFormElement>) {
    if (mode !== 'reply' || uploading) return;
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    if (!dragging) setDragging(true);
  }
  function handleDragLeave(e: DragEvent<HTMLFormElement>) {
    // Игнорируем dragleave на дочерних элементах (перелёт между ними).
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragging(false);
  }
  function handleDrop(e: DragEvent<HTMLFormElement>) {
    e.preventDefault();
    setDragging(false);
    const file = firstDroppedFile(e.dataTransfer);
    if (file) void uploadAndSend(file);
  }

  // ANX-076 — вставка картинки из буфера (скриншот). Текст вставляется
  // браузером как обычно; перехватываем только файлы.
  function handlePaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    if (mode !== 'reply') return;
    const file = firstPastedFile(e.clipboardData);
    if (!file) return;
    e.preventDefault();
    void uploadAndSend(file);
  }

  return (
    <div className="flex h-full min-h-0">
    <div className="flex min-w-0 flex-1 flex-col bg-slate-50">
      <header className="flex flex-col gap-2 border-b border-slate-200 bg-white px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <VisitorHeader conv={conv} conversationId={conversationId} t={t} />
          <div className="flex flex-col items-end gap-1 text-xs text-slate-500">
            {/* ANX-077 — поиск по сообщениям внутри диалога. */}
            {searchOpen ? (
              <ConversationSearch
                conversationId={conversationId}
                onNavigate={onSearchNavigate}
                onActiveQueryChange={setHighlightQuery}
                onClose={onSearchClose}
              />
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label={t('conversation.search.open')}
                className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
              >
                <Search size={14} aria-hidden="true" />
              </button>
            )}
            {conv && <CloseControl conv={conv} conversationId={conversationId} />}
            <AssignmentControl
              conv={conv}
              conversationId={conversationId}
              currentOperatorId={session?.operator.id}
              t={t}
            />
            {conv && (
              <TransferControl
                conversationId={conversationId}
                currentOperatorId={session?.operator.id}
                t={t}
              />
            )}
            {conv && (
              <BanVisitorControl
                conv={conv}
                operatorRole={session?.operator.role}
                t={t}
              />
            )}
            {conv && <TranscriptSelfButton conversationId={conversationId} t={t} />}
            <span>{wsLabel(wsState, t)}</span>
            {visitorTyping && (
              <span className="italic text-slate-500">{t('conversation.visitorTyping')}</span>
            )}
          </div>
        </div>
        {/* ANX-043 — chip'ы тегов + popover «Добавить». Рендерим всегда (для
            попап-кнопки), даже если у диалога ещё нет тегов. */}
        {conv && <TagsRow conv={conv} conversationId={conversationId} t={t} />}
        {/* ANX-072 — выбранный визитёром отдел (если есть). */}
        {conv && conv.departmentId && <DepartmentBadge departmentId={conv.departmentId} t={t} />}
        {/* ANX-062 — оценка визитёра (если оставлена). */}
        {conv && conv.csatScore !== undefined && (
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-medium">{t('conversation.csat.label')}</span>
            <span aria-label={conv.csatScore === 1 ? t('conversations.csat.positive') : t('conversations.csat.negative')}>
              {conv.csatScore === 1 ? '👍' : '👎'}
            </span>
            {conv.csatComment && (
              <span className="italic text-slate-500">{`«${conv.csatComment}»`}</span>
            )}
          </div>
        )}
      </header>

      <div
        ref={scrollRef}
        onScroll={onMessagesScroll}
        className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-slate-50 px-5 py-4"
      >
        {isLoading && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {buildTimeline(messages, notes).map((it) =>
          it.kind === 'message' ? (
            <MessageRow
              key={it.m.id}
              m={it.m}
              readBySeq={visitorReadSeq}
              t={t}
              highlightQuery={highlightQuery}
              activeSeq={activeSeq}
              onReply={(msg) => {
                setMode('reply');
                setReplyTo(msg);
                inputRef.current?.focus();
              }}
              quoted={it.m.replyToId ? messagesById.get(it.m.replyToId) : undefined}
              onQuoteClick={onSearchNavigate}
            />
          ) : (
            <NoteRow key={it.n.id} n={it.n} t={t} />
          ),
        )}
        {visitorTyping && <TypingDotsRow t={t} />}
      </div>

      <form
        onSubmit={handleSubmit}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={
          'relative flex flex-col gap-2 border-t px-5 py-3 ' +
          (mode === 'note' ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white')
        }
      >
        {dragging && (
          <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-md border-2 border-dashed border-accent-400 bg-accent-50/90 text-sm font-medium text-accent-700">
            {t('conversation.dropHint')}
          </div>
        )}
        {/* ANX-063 — переключатель «Ответ / Заметка». */}
        <div className="flex gap-1 text-xs" role="tablist" aria-label={t('conversation.note.modeLabel')}>
          {(['reply', 'note'] as const).map((mr) => (
            <button
              key={mr}
              type="button"
              role="tab"
              aria-selected={mode === mr}
              onClick={() => {
                setMode(mr);
                setPickerActive(false);
                setMentionActive(false);
              }}
              className={
                'rounded-md px-2.5 py-1 ' +
                (mode === mr
                  ? mr === 'note'
                    ? 'bg-amber-200 text-amber-900 font-medium'
                    : 'bg-accent-50 text-accent-700 font-medium'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700')
              }
            >
              {mr === 'reply' ? t('conversation.note.modeReply') : t('conversation.note.modeNote')}
            </button>
          ))}
          {mode === 'note' && (
            <span className="self-center pl-1 text-amber-600">{t('conversation.note.hint')}</span>
          )}
        </div>
        {mode === 'reply' && replyTo && (
          <div className="flex items-center gap-2 rounded-md border-l-2 border-accent-400 bg-accent-50 px-2 py-1 text-xs">
            <Reply size={12} className="shrink-0 text-accent-600" aria-hidden="true" />
            <span className="font-medium text-accent-700">
              {replyTo.senderType === 'operator'
                ? t('conversation.reply.you')
                : t('conversation.reply.visitor')}
            </span>
            <span className="min-w-0 flex-1 truncate text-slate-600">
              {messageExcerpt(replyTo.body, 80)}
            </span>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              aria-label={t('conversation.reply.cancel')}
              className="shrink-0 text-slate-400 hover:text-slate-600"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="relative flex gap-2">
          {mode === 'reply' && pickerActive && matchedCanned.length > 0 && (
            <div
              className="absolute bottom-full left-0 right-0 z-10 mb-1 max-h-60 overflow-auto rounded-md border border-slate-200 bg-white shadow-pop"
              role="listbox"
            >
              {matchedCanned.map((cr, i) => (
                <button
                  key={cr.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyCanned(cr);
                  }}
                  className={
                    'w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 ' +
                    (i === pickerIndex ? 'bg-slate-100' : 'hover:bg-slate-50')
                  }
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-slate-900">{cr.title}</span>
                    {cr.shortcut && (
                      <span className="font-mono text-xs text-slate-500">{cr.shortcut}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{cr.body}</div>
                </button>
              ))}
            </div>
          )}
          {/* ANX-063 — @mention autocomplete (note-режим). */}
          {mode === 'note' && mentionActive && matchedOperators.length > 0 && (
            <div
              className="absolute bottom-full left-0 right-0 z-10 mb-1 max-h-60 overflow-auto rounded-md border border-amber-200 bg-white shadow-pop"
              role="listbox"
            >
              {matchedOperators.map((op, i) => (
                <button
                  key={op.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyMention(op);
                  }}
                  className={
                    'w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 ' +
                    (i === mentionIndex ? 'bg-amber-100' : 'hover:bg-amber-50')
                  }
                >
                  <span className="font-medium text-slate-900">{op.name}</span>
                  <span className="ml-2 text-xs text-slate-500">{op.email}</span>
                </button>
              ))}
            </div>
          )}
          {mode === 'reply' && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_MIME}
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center justify-center rounded-md border border-slate-200 px-3 text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50"
                aria-label={t('conversation.attachFile')}
              >
                <Paperclip size={16} aria-hidden="true" />
              </button>
              <EmojiPicker onSelect={insertEmoji} disabled={uploading} />
            </>
          )}
          <textarea
            ref={inputRef}
            rows={1}
            value={draft}
            disabled={uploading}
            onPaste={handlePaste}
            onChange={(e) => {
              const v = e.target.value;
              setDraft(v);
              if (mode === 'note') {
                const m = /@(\w*)$/.test(v);
                setMentionActive(m);
                if (m) setMentionIndex(0);
              } else {
                const active = v.startsWith('/');
                setPickerActive(active);
                if (active) setPickerIndex(0);
                if (v.length > 0) sendTypingStart();
                else sendTypingStop();
              }
            }}
            onKeyDown={(e) => {
              const picking = mode === 'note' ? mentionActive && matchedOperators.length > 0 : pickerActive && matchedCanned.length > 0;
              if (picking) {
                const len = mode === 'note' ? matchedOperators.length : matchedCanned.length;
                const idx = mode === 'note' ? mentionIndex : pickerIndex;
                const setIdx = mode === 'note' ? setMentionIndex : setPickerIndex;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setIdx(Math.min(idx + 1, len - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setIdx(Math.max(idx - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  if (mode === 'note') applyMention(matchedOperators[mentionIndex]);
                  else applyCanned(matchedCanned[pickerIndex]);
                } else if (e.key === 'Escape') {
                  if (mode === 'note') setMentionActive(false);
                  else setPickerActive(false);
                }
                return;
              }
              // ANX-116: Enter отправляет, Shift+Enter — перенос строки.
              if (isSendKey({ key: e.key, shiftKey: e.shiftKey, isComposing: e.nativeEvent.isComposing })) {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }}
            placeholder={
              mode === 'note'
                ? t('conversation.note.placeholder')
                : uploading
                  ? t('conversation.uploadingPlaceholder')
                  : t('conversation.placeholder')
            }
            className={
              'flex-1 resize-none max-h-40 overflow-y-auto rounded-md border px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 ' +
              (mode === 'note'
                ? 'border-amber-300 bg-white focus:border-amber-400 focus:ring-amber-100'
                : 'border-slate-200 bg-white focus:border-accent-400 focus:ring-accent-100')
            }
          />
          <button
            type="submit"
            disabled={draft.trim() === '' || uploading}
            className={
              'inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300 ' +
              (mode === 'note' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-accent-600 hover:bg-accent-700')
            }
          >
            {mode === 'note' ? <StickyNote size={14} aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
            {mode === 'note' ? t('conversation.note.send') : t('conversation.send')}
          </button>
        </div>
      </form>
      {sendError !== null && (
        <p role="alert" className="bg-white px-5 pb-3 text-xs text-red-600">
          {sendError}
        </p>
      )}
    </div>
    {conv && <VisitorContextPanel visitorId={conv.visitorId} t={t} />}
    </div>
  );
}

/**
 * VisitorHeader — шапка ConversationView с контактами visitor'а (ANX-029).
 *
 * Заголовок — имя visitor'а, либо локализованный fallback "Гость".
 * Под именем — chip'ы email и phone с кнопкой «копировать». Если
 * visitor не оставил контактов вовсе, рендерим короткую пометку
 * (oператор сразу понимает, что fallback-канала нет).
 *
 * conv может быть undefined пока useQuery ещё грузится — рендерим placeholder.
 */
function VisitorHeader({
  conv,
  conversationId,
  t,
}: {
  conv: Conversation | undefined;
  conversationId: string;
  t: TFn;
}) {
  const v = conv?.visitor;
  const displayName =
    v?.name?.trim() ||
    v?.email?.trim() ||
    `${t('conversations.visitor.fallback')} ${conversationId.slice(0, 8)}`;
  const email = v?.email?.trim();
  const phone = v?.phone?.trim();
  const hasContacts = !!(email || phone);

  return (
    <div className="min-w-0">
      <h2 className="truncate text-sm font-semibold text-slate-900">{displayName}</h2>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
        {email && (
          <CopyableContact
            label={t('conversations.visitor.email')}
            value={email}
            t={t}
          />
        )}
        {phone && (
          <CopyableContact
            label={t('conversations.visitor.phone')}
            value={phone}
            t={t}
          />
        )}
        {!hasContacts && conv && (
          <span className="italic">{t('conversations.visitor.noContacts')}</span>
        )}
      </div>
    </div>
  );
}

function CopyableContact({ label, value, t }: { label: string; value: string; t: TFn }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Игнорируем — старые браузеры без Clipboard API. UI не блокируем.
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-slate-400">{label}:</span>
      <span className="text-slate-700">{value}</span>
      <button
        type="button"
        onClick={() => void onCopy()}
        className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent-600 hover:bg-accent-50"
        aria-label={t('conversations.visitor.copy')}
      >
        {copied ? t('conversations.visitor.copied') : t('conversations.visitor.copy')}
      </button>
    </span>
  );
}

function MessageRow({
  m,
  readBySeq,
  t,
  highlightQuery = '',
  activeSeq = 0,
  onReply,
  quoted,
  onQuoteClick,
}: {
  m: OptimisticMessage;
  readBySeq: number;
  t: TFn;
  highlightQuery?: string;
  activeSeq?: number;
  // ANX-119 — ответить на это сообщение.
  onReply?: (m: OptimisticMessage) => void;
  // Резолвнутое сообщение-оригинал для цитаты (undefined → выпало из окна).
  quoted?: OptimisticMessage;
  onQuoteClick?: (seq: number) => void;
}) {
  const own = m.senderType === 'operator';
  const hasText = m.body.trim().length > 0;
  const isRead = own && !m.pending && !m.failed && m.seq > 0 && m.seq <= readBySeq;
  // ANX-077 — активное совпадение поиска подсвечивается кольцом.
  const isActiveMatch = highlightQuery !== '' && m.seq > 0 && m.seq === activeSeq;
  // ANX-119 — кнопка «Ответить» доступна только для реальных (не pending) сообщений.
  const canReply = onReply && !m.pending && !m.failed && m.seq > 0;
  return (
    <div
      className={
        'group flex items-center gap-1.5 ' + (own ? 'justify-end' : 'justify-start')
      }
      data-seq={m.seq > 0 ? m.seq : undefined}
    >
      {own && canReply && (
        <ReplyButton onClick={() => onReply?.(m)} label={t('conversation.reply')} />
      )}
      <div
        className={
          'max-w-[70%] rounded-2xl px-3.5 py-2 text-sm shadow-card ' +
          (own
            ? 'bg-accent-600 text-white'
            : 'border border-slate-200 bg-white text-slate-900') +
          (isActiveMatch ? ' ring-2 ring-amber-400' : '')
        }
      >
        {m.replyToId && (
          <QuotedSnippet
            quoted={quoted}
            own={own}
            t={t}
            onClick={quoted && onQuoteClick ? () => onQuoteClick(quoted.seq) : undefined}
          />
        )}
        {m.attachments && m.attachments.length > 0 && (
          <div className="mb-1 flex flex-col gap-1">
            {m.attachments.map((a) => (
              <AttachmentView key={a.key} a={a} />
            ))}
          </div>
        )}
        {hasText && (
          <div className="whitespace-pre-wrap break-words">
            {highlightQuery === ''
              ? // ANX-115 — кликабельные ссылки (всегда новая вкладка: оператор
                // в админ-панели, нельзя уводить его со страницы).
                parseLinks(m.body).map((p, i) =>
                  p.href !== undefined ? (
                    <a
                      key={i}
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 break-all"
                    >
                      {p.text}
                    </a>
                  ) : (
                    <span key={i}>{p.text}</span>
                  ),
                )
              : highlightParts(m.body, highlightQuery).map((p, i) =>
                  p.match ? (
                    <mark key={i} className="rounded bg-amber-200 text-slate-900">
                      {p.text}
                    </mark>
                  ) : (
                    <span key={i}>{p.text}</span>
                  ),
                )}
          </div>
        )}
        {m.pending && (
          <div className="mt-1 text-[10px] opacity-70">{t('conversation.sending')}</div>
        )}
        {m.failed && (
          <div className="mt-1 text-[10px] text-red-300">{t('conversation.failed')}</div>
        )}
        {own && !m.pending && !m.failed && (
          <div
            className="mt-1 inline-flex items-center text-accent-100/80"
            aria-label={isRead ? t('conversation.read') : t('conversation.delivered')}
          >
            {isRead ? <CheckCheck size={12} /> : <Check size={12} />}
          </div>
        )}
      </div>
      {!own && canReply && (
        <ReplyButton onClick={() => onReply?.(m)} label={t('conversation.reply')} />
      )}
    </div>
  );
}

// ANX-119 — кнопка «Ответить» рядом с пузырём (видна по hover на строку).
function ReplyButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="shrink-0 rounded-full p-1.5 text-slate-400 opacity-0 transition hover:bg-slate-200 hover:text-slate-600 group-hover:opacity-100"
    >
      <Reply size={14} aria-hidden="true" />
    </button>
  );
}

// ANX-119 — превью цитаты внутри пузыря. quoted === undefined → оригинал выпал
// из окна истории, показываем нейтральную плашку без скролла.
function QuotedSnippet({
  quoted,
  own,
  t,
  onClick,
}: {
  quoted?: OptimisticMessage;
  own: boolean;
  t: TFn;
  onClick?: () => void;
}) {
  const sender = quoted
    ? quoted.senderType === 'operator'
      ? t('conversation.reply.you')
      : t('conversation.reply.visitor')
    : '';
  const text = quoted ? messageExcerpt(quoted.body, 80) : t('conversation.reply.unavailable');
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={
        'mb-1 block w-full rounded-md border-l-2 px-2 py-1 text-left text-xs ' +
        (own
          ? 'border-white/60 bg-white/15 text-white/90'
          : 'border-accent-400 bg-slate-50 text-slate-600') +
        (onClick ? ' cursor-pointer hover:opacity-80' : ' cursor-default')
      }
    >
      {quoted && <span className="font-medium">{sender}</span>}
      <span className={quoted ? 'ml-1 opacity-80' : 'italic opacity-80'}>{text}</span>
    </button>
  );
}

// ANX-063 — единая лента: сообщения + внутренние заметки, отсортированы по
// времени. Заметки рендерятся отдельным NoteRow (жёлтый фон). Чистая функция
// (не hook) — список мал, recompute на рендер дёшев.
type TimelineItem =
  | { kind: 'message'; ts: number; m: OptimisticMessage }
  | { kind: 'note'; ts: number; n: ConversationNote };

function buildTimeline(
  messages: OptimisticMessage[] | undefined,
  notes: ConversationNote[] | undefined,
): TimelineItem[] {
  const items: TimelineItem[] = [];
  for (const m of messages ?? []) items.push({ kind: 'message', ts: Date.parse(m.createdAt), m });
  for (const n of notes ?? []) items.push({ kind: 'note', ts: Date.parse(n.createdAt), n });
  items.sort((a, b) => a.ts - b.ts);
  return items;
}

// NoteRow — внутренняя заметка оператора. Жёлтый фон визуально отделяет от
// настоящих сообщений. @упоминания подсвечиваются (см. renderNoteBody).
function NoteRow({ n, t }: { n: ConversationNote; t: TFn }) {
  return (
    <div className="flex justify-center">
      <div className="w-full max-w-[85%] rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
        <div className="mb-0.5 flex items-center gap-1.5 text-xs text-amber-700">
          <StickyNote size={12} aria-hidden="true" />
          <span className="font-medium">{n.operatorName || t('conversation.note.author')}</span>
          <span className="text-amber-500">{t('conversation.note.badge')}</span>
        </div>
        <div className="whitespace-pre-wrap break-words text-amber-900">{renderNoteBody(n.body)}</div>
      </div>
    </div>
  );
}

// renderNoteBody подсвечивает @-токены (имена-одно-слово; для имён с пробелом
// подсветится первое слово — достаточно для визуального акцента).
function renderNoteBody(body: string): Array<string | ReactElement> {
  const parts: Array<string | ReactElement> = [];
  const re = /@\w+/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    parts.push(
      <strong key={`m${i++}`} className="font-semibold text-amber-800">
        {m[0]}
      </strong>,
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return parts;
}

// ANX-064 — контекст-панель справа: страница, гео, устройство, кастомные
// атрибуты сайта, история прошлых обращений визитёра.
// ANX-072 — бейдж отдела диалога. Имя резолвим из списка отделов (общий
// query-кэш с DepartmentsAdmin/PreChatBlock). Если отдел удалён — не рисуем.
function DepartmentBadge({ departmentId, t }: { departmentId: string; t: TFn }) {
  const { data: departments } = useQuery({
    queryKey: ['departments'] as const,
    queryFn: () => listDepartments(getApiClient()),
  });
  const name = departments?.find((d) => d.id === departmentId)?.name;
  if (!name) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-slate-600">
      <span className="font-medium">{t('conversation.department.label')}</span>
      <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-700">{name}</span>
    </div>
  );
}

function VisitorContextPanel({ visitorId, t }: { visitorId: string; t: TFn }) {
  const { data, isLoading } = useQuery<VisitorContext>({
    queryKey: ['visitor_context', visitorId],
    queryFn: () => getVisitorContext(getApiClient(), visitorId),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
  return (
    <aside className="hidden w-72 shrink-0 overflow-y-auto border-l border-slate-200 bg-white px-4 py-4 xl:block">
      <h2 className="mb-3 text-sm font-semibold text-slate-900">{t('context.title')}</h2>
      {isLoading && <p className="text-xs text-slate-500">{t('common.loading')}</p>}
      {data && <VisitorContextBody c={data} t={t} />}
    </aside>
  );
}

function ContextField({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="mb-1.5">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="break-words text-xs text-slate-700">{value}</div>
    </div>
  );
}

// parseUserAgent — грубое извлечение браузера и ОС из UA-строки. Без внешних
// либ: покрывает массовые случаи, иначе показываем сырой UA.
function parseUserAgent(ua: string): string {
  let browser = '';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  let os = '';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iOS/.test(ua)) os = 'iOS';
  else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  const parts = [browser, os].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : ua;
}

function VisitorContextBody({ c, t }: { c: VisitorContext; t: TFn }) {
  const geo = [c.geoCity, c.geoCountry].filter(Boolean).join(', ');
  const attrEntries = Object.entries(c.attributes);
  return (
    <div>
      <ContextField label={t('context.page')} value={c.lastUrl} />
      <ContextField label={t('context.referrer')} value={c.referrer} />
      <ContextField label={t('context.location')} value={geo || null} />
      <ContextField label={t('context.device')} value={c.userAgent ? parseUserAgent(c.userAgent) : null} />
      <ContextField label={t('context.screen')} value={c.screen} />
      <ContextField label={t('context.language')} value={c.language} />

      {attrEntries.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="mb-1.5 text-[11px] uppercase tracking-wide text-slate-400">
            {t('context.attributes')}
          </div>
          <dl className="space-y-1">
            {attrEntries.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 text-xs">
                <dt className="text-slate-500">{k}</dt>
                <dd className="break-words text-right font-medium text-slate-800">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="mt-3 border-t border-slate-100 pt-3">
        <div className="mb-1.5 text-[11px] uppercase tracking-wide text-slate-400">
          {t('context.history')}
        </div>
        {c.pastConversations.length === 0 ? (
          <p className="text-xs text-slate-400">{t('context.noHistory')}</p>
        ) : (
          <ul className="space-y-1">
            {c.pastConversations.map((p) => (
              <li key={p.id}>
                <RouterLink
                  to="/conversations/$id"
                  params={{ id: p.id }}
                  className="flex items-center justify-between gap-2 rounded px-1.5 py-1 text-xs hover:bg-slate-50"
                >
                  <span className="text-slate-600">
                    {p.status === 'open' ? t('conversations.filter.open') : t('conversations.filter.closed')}
                  </span>
                  <span className="flex items-center gap-1 text-slate-400">
                    {p.csatScore !== undefined && <span>{p.csatScore === 1 ? '👍' : '👎'}</span>}
                    {p.createdAt.slice(0, 10)}
                  </span>
                </RouterLink>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TypingDotsRow({ t }: { t: TFn }) {
  return (
    <div className="flex justify-start" aria-label={t('conversation.visitorTypingAria')}>
      <div className="px-3 py-2 rounded bg-white border border-slate-200 inline-flex gap-1">
        <span className="text-slate-400 text-xs leading-none">●</span>
        <span className="text-slate-500 text-xs leading-none">●</span>
        <span className="text-slate-600 text-xs leading-none">●</span>
      </div>
    </div>
  );
}

function AttachmentView({ a }: { a: Attachment }) {
  const isImage = a.mimeType.startsWith('image/');
  if (isImage && a.url) {
    return (
      <a href={a.url} target="_blank" rel="noreferrer">
        <img src={a.url} alt={a.name} className="max-h-[200px] max-w-[220px] rounded-lg" />
      </a>
    );
  }
  if (a.url) {
    return (
      <a
        href={a.url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-xs underline"
      >
        <FileIcon size={12} aria-hidden="true" /> {a.name}
      </a>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <FileIcon size={12} aria-hidden="true" /> {a.name}
    </span>
  );
}

function wsLabel(s: ConnectionState, t: TFn): string {
  switch (s) {
    case 'online':
      return t('conversation.online');
    case 'connecting':
      return t('conversation.connecting');
    case 'offline':
      return t('conversation.offline');
  }
}

/**
 * AssignmentControl — ANX-047. Chip-кнопка с именем назначенного оператора
 * (или "Не назначено") + popover-меню «Взять себе / Передать / Снять».
 *
 * Список операторов грузится лениво, при первом клике (staleTime 5 min).
 * После patch'а — invalidate ['conversations'] + setQueryData на текущий conv,
 * чтобы UI обновился без ожидания polling'а.
 */
function AssignmentControl({
  conv,
  conversationId,
  currentOperatorId,
  t,
}: {
  conv: Conversation | undefined;
  conversationId: string;
  currentOperatorId: string | undefined;
  t: TFn;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const { data: operators } = useQuery({
    queryKey: ['operators'] as const,
    queryFn: () => listOperators(getApiClient()),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  if (!conv) return null;

  const assignedId = conv.assignedOperatorId;
  const assignedName =
    assignedId !== null && operators
      ? operators.find((o) => o.id === assignedId)?.name
      : undefined;

  async function assignTo(operatorId: string | null) {
    if (pending) return;
    setPending(true);
    try {
      const updated = await patchConversation(getApiClient(), conversationId, {
        ...(operatorId === null
          ? { unassignOperator: true }
          : { assignedOperatorId: operatorId }),
      });
      queryClient.setQueryData<Conversation>(['conversation', conversationId], (prev) =>
        mergeConversation(prev, updated),
      );
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      setOpen(false);
    } finally {
      setPending(false);
    }
  }

  const chipLabel = assignedId
    ? assignedId === currentOperatorId
      ? t('conversation.assigned.me')
      : assignedName || t('conversation.assigned.other')
    : t('conversation.assigned.none');

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ' +
          (assignedId
            ? 'border-accent-200 bg-accent-50 text-accent-700'
            : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100')
        }
      >
        <UserPlus size={12} aria-hidden="true" />
        {chipLabel}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-1 w-56 rounded-md border border-slate-200 bg-white py-1 text-xs shadow-pop">
          {assignedId !== currentOperatorId && currentOperatorId && (
            <button
              type="button"
              disabled={pending}
              onClick={() => void assignTo(currentOperatorId)}
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:opacity-50"
            >
              {t('conversation.assign.takeMe')}
            </button>
          )}
          {assignedId !== null && (
            <button
              type="button"
              disabled={pending}
              onClick={() => void assignTo(null)}
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:opacity-50"
            >
              {t('conversation.assign.unassign')}
            </button>
          )}
          {operators && operators.length > 0 && (
            <div className="my-1 border-t border-slate-100" />
          )}
          {operators &&
            operators
              .filter((o) => o.id !== assignedId && o.id !== currentOperatorId)
              .map((o) => (
                <button
                  key={o.id}
                  type="button"
                  disabled={pending}
                  onClick={() => void assignTo(o.id)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 disabled:opacity-50"
                  title={o.email}
                >
                  <StatusDot status={effectiveStatus(o)} />
                  <span className="truncate">{o.name || o.email}</span>
                </button>
              ))}
        </div>
      )}
    </div>
  );
}

/**
 * TransferControl — ANX-074. Кнопка «Передать» в шапке диалога: выбор коллеги
 * + опц. заметка-причина → POST /transfer (реассайн + заметка в ленте + push
 * получателю). Передача на away/busy не блокируется (ручное действие).
 */
function TransferControl({
  conversationId,
  currentOperatorId,
  t,
}: {
  conversationId: string;
  currentOperatorId: string | undefined;
  t: TFn;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('');
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(false);
  const { data: operators } = useQuery({
    queryKey: ['operators'] as const,
    queryFn: () => listOperators(getApiClient()),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const candidates = (operators ?? []).filter(
    (o) => o.id !== currentOperatorId && effectiveStatus(o) !== undefined,
  );

  async function submit() {
    if (pending || target === '') return;
    setPending(true);
    try {
      const updated = await transferConversation(getApiClient(), conversationId, target, note);
      queryClient.setQueryData<Conversation>(['conversation', conversationId], (prev) =>
        mergeConversation(prev, updated),
      );
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId, 'notes'] });
      setOpen(false);
      setTarget('');
      setNote('');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-100"
      >
        <ArrowRightLeft size={12} aria-hidden="true" />
        {t('conversation.transfer.button')}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-md border border-slate-200 bg-white p-2 text-xs shadow-pop">
          <select
            value={target}
            onChange={(e) => setTarget(e.currentTarget.value)}
            className="mb-2 block w-full rounded border border-slate-300 px-2 py-1 text-xs focus:border-accent-500 focus:outline-none"
          >
            <option value="">{t('conversation.transfer.selectOperator')}</option>
            {candidates.map((o) => (
              <option key={o.id} value={o.id}>{o.name || o.email}</option>
            ))}
          </select>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            placeholder={t('conversation.transfer.notePlaceholder')}
            className="mb-2 block w-full rounded border border-slate-300 px-2 py-1 text-xs focus:border-accent-500 focus:outline-none"
          />
          <button
            type="button"
            disabled={pending || target === ''}
            onClick={() => void submit()}
            className="w-full rounded bg-slate-900 px-2 py-1 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {pending ? t('conversation.transfer.sending') : t('conversation.transfer.submit')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * BanVisitorControl — ANX-046. Кнопка-chip «Заблокировать», открывает
 * popover с выбором TTL + textarea reason. Видимость: admin всегда;
 * operator только если tenant.security-policy.ban_policy='any_operator'.
 * Policy подтягивается лениво при первом mount'е, чтобы не делать лишний
 * запрос для operator-ов (мы рендерим chip только после успешного fetch'а).
 */
function BanVisitorControl({
  conv,
  operatorRole,
  t,
}: {
  conv: Conversation;
  operatorRole: string | undefined;
  t: TFn;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [reason, setReason] = useState('');
  const [ttl, setTtl] = useState<'1d' | '7d' | '30d' | 'perm'>('7d');
  const [error, setError] = useState<string | null>(null);

  // Operator (не admin) — тянем policy, чтобы решить, рендерить ли кнопку.
  // Admin'у policy не нужна: он всегда может банить.
  const isAdmin = operatorRole === 'admin';
  const { data: policy } = useQuery({
    queryKey: ['tenant_security_policy'] as const,
    queryFn: () => getTenantSecurityPolicy(getApiClient()),
    enabled: !isAdmin,
    staleTime: 5 * 60 * 1000,
  });
  const canBan = isAdmin || policy?.banPolicy === 'any_operator';
  if (!canBan) return null;

  const visitorID = conv.visitorId;

  function ttlToSeconds(v: typeof ttl): number {
    switch (v) {
      case '1d':
        return 86400;
      case '7d':
        return 7 * 86400;
      case '30d':
        return 30 * 86400;
      default:
        return 0; // permanent
    }
  }

  async function confirmBan() {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await banVisitor(getApiClient(), visitorID, {
        durationSeconds: ttlToSeconds(ttl),
        reason: reason.trim(),
      });
      setOpen(false);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['visitor_bans'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('ban.error'));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] text-red-700 hover:bg-red-100"
      >
        <Ban size={12} aria-hidden="true" />
        {t('ban.button')}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-md border border-slate-200 bg-white p-3 text-xs shadow-pop">
          <p className="mb-2 text-slate-700">{t('ban.popover.title')}</p>
          <div className="mb-2 grid grid-cols-4 gap-1">
            {(['1d', '7d', '30d', 'perm'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setTtl(opt)}
                className={
                  'rounded px-1 py-1 text-[10px] font-medium ' +
                  (ttl === opt
                    ? 'bg-red-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200')
                }
              >
                {t(`ban.ttl.${opt}`)}
              </button>
            ))}
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder={t('ban.reason.placeholder')}
            maxLength={500}
            rows={2}
            className="mb-2 w-full resize-none rounded border border-slate-300 px-2 py-1 text-xs focus:border-accent-500 focus:outline-none"
          />
          {error && (
            <p role="alert" className="mb-2 text-[11px] text-red-600">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              disabled={pending}
              onClick={() => setOpen(false)}
              className="rounded px-2 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              {t('ban.cancel')}
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={() => void confirmBan()}
              className="rounded bg-red-600 px-2 py-1 font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {pending ? t('ban.saving') : t('ban.confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * TagsRow — ANX-043. Chip'ы тегов в шапке ConversationView + popover
 * «Добавить тег». Список tag'ов tenant'а грузится лениво при первом
 * клике на кнопку «+». После assign/unassign — invalidate ['conversations']
 * и обновление кэша одного диалога, чтобы UI обновился без polling'а.
 */
function TagsRow({
  conv,
  conversationId,
  t,
}: {
  conv: Conversation;
  conversationId: string;
  t: TFn;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  // ANX-118 — фильтр/ввод имени для создания нового тега на лету.
  const [filter, setFilter] = useState('');
  const tags = conv.tags ?? [];
  const { data: allTags } = useQuery({
    queryKey: ['conversation_tags'] as const,
    queryFn: () => listConversationTags(getApiClient()),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  // ANX-118 — создать тег по введённому имени и сразу повесить на диалог.
  async function createAndAssign() {
    const name = filter.trim();
    if (pending || name === '') return;
    setPending(true);
    try {
      const color = pickTagColor(allTags?.length ?? 0);
      const created = await createConversationTag(getApiClient(), { name, color });
      await assignTagToConversation(getApiClient(), conversationId, created.id);
      setFilter('');
      void queryClient.invalidateQueries({ queryKey: ['conversation_tags'] });
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    } finally {
      setPending(false);
    }
  }

  async function toggleTag(tag: ConversationTag) {
    if (pending) return;
    setPending(true);
    const already = tags.some((x) => x.id === tag.id);
    try {
      if (already) {
        await unassignTagFromConversation(getApiClient(), conversationId, tag.id);
      } else {
        await assignTagToConversation(getApiClient(), conversationId, tag.id);
      }
      // Перезагружаем одну конкретную конверсацию + rail.
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    } finally {
      setPending(false);
    }
  }

  async function removeChip(tag: ConversationTagRef) {
    if (pending) return;
    setPending(true);
    try {
      await unassignTagFromConversation(getApiClient(), conversationId, tag.id);
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    } finally {
      setPending(false);
    }
  }

  const picker = tagPickerState(allTags ?? [], filter);

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
      {tags.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
          style={{
            backgroundColor: tag.color + '22',
            borderColor: tag.color + '66',
            color: tag.color,
          }}
        >
          {tag.name}
          <button
            type="button"
            onClick={() => void removeChip(tag)}
            disabled={pending}
            aria-label={t('tags.removeChip')}
            className="opacity-60 hover:opacity-100 disabled:opacity-30"
          >
            <X size={10} aria-hidden="true" />
          </button>
        </span>
      ))}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-50"
        >
          <TagIcon size={10} aria-hidden="true" />
          {t('tags.add')}
        </button>
        {open && (
          <div className="absolute left-0 top-full z-10 mt-1 max-h-64 w-56 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-xs shadow-pop">
            <div className="px-2 pb-1 pt-0.5">
              <input
                type="text"
                autoFocus
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (picker.canCreate) void createAndAssign();
                  } else if (e.key === 'Escape') {
                    setOpen(false);
                  }
                }}
                placeholder={t('tags.searchOrCreate')}
                className="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-accent-400 focus:outline-none"
              />
            </div>
            {!allTags ? (
              <p className="px-3 py-2 text-slate-500">{t('common.loading')}</p>
            ) : (
              <>
                {picker.filtered.map((tag) => {
                  const selected = tags.some((x) => x.id === tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      disabled={pending}
                      onClick={() => void toggleTag(tag)}
                      className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-slate-50 disabled:opacity-50"
                    >
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 rounded-full"
                          style={{ backgroundColor: tag.color }}
                          aria-hidden="true"
                        />
                        <span>{tag.name}</span>
                      </span>
                      {selected && <Check size={12} aria-hidden="true" />}
                    </button>
                  );
                })}
                {picker.canCreate && (
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => void createAndAssign()}
                    className="flex w-full items-center gap-2 border-t border-slate-100 px-3 py-1.5 text-left text-accent-700 hover:bg-accent-50 disabled:opacity-50"
                  >
                    <Plus size={12} aria-hidden="true" />
                    <span>{`${t('tags.create')} «${picker.name}»`}</span>
                  </button>
                )}
                {picker.filtered.length === 0 && !picker.canCreate && (
                  <p className="px-3 py-2 text-slate-500">{t('tags.empty')}</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * TranscriptSelfButton — ANX-050. Кнопка «Прислать копию диалога мне» в
 * шапке conversation. Оператор может слать сколько хочет (idempotency-флаг
 * conversations.transcript_sent_at не трогается, см. worker).
 */
function TranscriptSelfButton({
  conversationId,
  t,
}: {
  conversationId: string;
  t: TFn;
}) {
  const [state, setState] = useState<'idle' | 'pending' | 'done' | 'error'>('idle');

  async function send() {
    if (state === 'pending') return;
    setState('pending');
    try {
      await sendTranscriptToOperator(getApiClient(), conversationId);
      setState('done');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 3000);
    }
  }

  const label =
    state === 'pending'
      ? t('conversation.transcript.sending')
      : state === 'done'
      ? t('conversation.transcript.sent')
      : state === 'error'
      ? t('conversation.transcript.error')
      : t('conversation.transcript.sendToMe');

  return (
    <button
      type="button"
      onClick={() => void send()}
      disabled={state === 'pending'}
      className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50 disabled:opacity-50"
    >
      <Mail size={12} aria-hidden="true" />
      {label}
    </button>
  );
}
